import type { Feature, MultiPolygon as GeoJsonMultiPolygon, Polygon as GeoJsonPolygon } from 'geojson';
import { memo } from 'react';
import { PiTreeBold } from 'react-icons/pi';

import type { EnvironmentFeatureProperties, EnvironmentResponse } from '~api/services';
import { Polygon, SvgTooltip } from '../components';
import { useLayerTooltip } from '../hooks';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

type EnvironmentSitesLayerProps = {
  /** Environment sites GeoJSON feature collection */
  environmentSites: EnvironmentResponse;
};

type EnvironmentFeature = Feature<GeoJsonPolygon | GeoJsonMultiPolygon, EnvironmentFeatureProperties>;

/* -------------------------------------------------------------------------- */
/*                                  Constants                                 */
/* -------------------------------------------------------------------------- */

const TOOLTIP_CONTENT: { label: string; value: (feature: EnvironmentFeature) => string }[] = [
  {
    label: 'Distance',
    value: (f) => (f.properties.distance != null ? `${f.properties.distance.toFixed(0)} m` : 'N/A'),
  },
  {
    label: 'Feature Type',
    value: (f) => f.properties.FEATURE_TYPE || 'N/A',
  },
  {
    label: 'Vibration Limit',
    value: (f) => f.properties.blast_design_constraints?.vibration_limit_html || 'N/A',
  },
  {
    label: 'Flyrock SDoB',
    value: (f) => f.properties.blast_design_constraints?.sdob_applicable_limit_html || 'N/A',
  },
  {
    label: 'Overpressure Limit',
    value: (f) => f.properties.blast_design_constraints?.overpressure_limit_html || 'N/A',
  },
  {
    label: 'Dust Control',
    value: (f) => f.properties.blast_design_constraints?.dust_control_html || 'N/A',
  },
  {
    label: 'Comments',
    value: (f) => f.properties.COMMENTS || 'N/A',
  },
];

/* -------------------------------------------------------------------------- */
/*                                   Helpers                                  */
/* -------------------------------------------------------------------------- */

const getEnvironmentFeatureId = (f: EnvironmentFeature) => String(f.properties.OBJECTID);

/** Normalises Polygon and MultiPolygon geometries into a flat array of Polygons for rendering. */
const getFeaturePolygons = (feature: EnvironmentFeature): GeoJsonPolygon[] =>
  feature.geometry.type === 'MultiPolygon'
    ? feature.geometry.coordinates.map((coords) => ({ type: 'Polygon' as const, coordinates: coords }))
    : [feature.geometry as GeoJsonPolygon];

/* -------------------------------------------------------------------------- */
/*                               Main component                               */
/* -------------------------------------------------------------------------- */

/**
 * Layer to render environment sites as polygons on the canvas.
 *
 * Wrapped in `memo` to avoid re-rendering on every D3 zoom/pan event triggered
 * by the parent canvas. The `environmentSites` reference is stable between zooms
 * since it comes from the TanStack Query cache.
 *
 * @example
 * ```tsx
 * <EnvironmentSitesLayer environmentSites={environmentSites} />
 * ```
 */
const EnvironmentSitesLayer = memo(({ environmentSites }: EnvironmentSitesLayerProps) => {
  const { layerRef, tooltipRef, hoveredItem } = useLayerTooltip({
    items: environmentSites.features,
    getId: getEnvironmentFeatureId,
  });

  if (environmentSites.features.length === 0) return null;

  return (
    <>
      <g className="environment-sites" ref={layerRef} aria-label="Environment sites layer">
        {environmentSites.features.map((feature) => {
          const polygons = getFeaturePolygons(feature);

          return (
            <g
              key={feature.properties.OBJECTID}
              className="environment-site"
              data-item-id={String(feature.properties.OBJECTID)}
              aria-label={`Environment site: ${feature.properties.SITE_ID}`}
            >
              {polygons.map((polygon, i) => (
                <Polygon
                  // biome-ignore lint/suspicious/noArrayIndexKey: Stable sub-polygon order within a site
                  key={i}
                  className="environment-site-polygon transition-[fill-opacity] duration-200 ease-in-out hover:cursor-pointer hover:[fill-opacity:0.5]"
                  geometry={polygon}
                  fill="var(--color-green-400)"
                  fillOpacity={0.15}
                  stroke="var(--color-green-500)"
                />
              ))}
            </g>
          );
        })}
      </g>
      <SvgTooltip ref={tooltipRef} visible={!!hoveredItem}>
        {hoveredItem && (
          <div className="flex flex-col">
            <h4 className="flex items-center gap-1 rounded-t-md bg-muted px-3 py-2 font-medium text-base">
              <PiTreeBold size={16} />
              {hoveredItem.properties.SITE_ID}
            </h4>
            <div className="flex flex-col gap-2 rounded-b-md px-3 py-2">
              {TOOLTIP_CONTENT.map(({ label, value }) => (
                <div key={label} className="flex items-center gap-1 text-sm">
                  <span className="font-bold">{label}:</span>
                  {/** biome-ignore lint/style/useNamingConvention: That's how `dangerouslySetInnerHTML` works with JSX  */}
                  {/** biome-ignore lint/security/noDangerouslySetInnerHtml: This is safe because the content is controlled and sanitized in the backend */}
                  <span dangerouslySetInnerHTML={{ __html: value(hoveredItem) }}></span>
                </div>
              ))}
            </div>
          </div>
        )}
      </SvgTooltip>
    </>
  );
});
EnvironmentSitesLayer.displayName = 'EnvironmentSitesLayer';

/* -------------------------------------------------------------------------- */
/*                                   Exports                                  */
/* -------------------------------------------------------------------------- */

export { EnvironmentSitesLayer, type EnvironmentSitesLayerProps };
